<template>
  <el-popover
    v-model:visible="visible"
    trigger="click"
    placement="bottom-end"
    :width="mode === 'reassign' ? 300 : 230"
    :show-arrow="false"
    popper-class="staff-picker-popper"
    @after-leave="mode = 'menu'"
  >
    <template #reference>
      <button type="button" class="actions-menu__trigger" title="More actions" aria-label="More actions">
        <EllipsisVertical :size="16" />
      </button>
    </template>

    <ul v-if="mode === 'menu'" class="actions-menu">
      <li v-for="item in items" :key="item.label">
        <button type="button" class="actions-menu__item" :disabled="busy" @click="item.run">
          <component :is="item.icon" :size="16" class="actions-menu__icon" /> {{ item.label }}
        </button>
      </li>
    </ul>

    <div v-else>
      <button type="button" class="actions-menu__back" @click="mode = 'menu'"><ChevronLeft :size="14" /> Assign</button>
      <StaffPickerList
        ref="picker"
        :staff="staffDirectory ?? []"
        :loading="isStaffDirectoryPending"
        :unassignable="conversation.assignee !== null"
        :assignable-to-me="conversation.assignee?.id !== currentStaffId"
        @select="onReassign"
      />
    </div>
  </el-popover>

  <ConversationHistoryDrawer v-model="historyVisible" :conversation="conversation" />
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue"
import type { Component } from "vue"
import { ElMessage, ElMessageBox } from "element-plus"
import {
  Archive,
  ArchiveRestore,
  ChevronLeft,
  CircleCheck,
  EllipsisVertical,
  History,
  UserRound
} from "lucide-vue-next"
import { PatientConversationDTO, PatientConversationStatusEnum } from "@vitable/asclepius-api-client-ts"
import StaffPickerList from "@/modules/patient-conversations/components/StaffPickerList.vue"
import ConversationHistoryDrawer from "@/modules/patient-conversations/components/thread/ConversationHistoryDrawer.vue"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"
import {
  useChangePatientConversationAssignment,
  useChangePatientConversationStatus
} from "@/modules/patient-conversations/composables/patient-conversations"
import { usePatientConversationStaffDirectory } from "@/modules/patient-conversations/composables/staff-directory"
import { describePatientConversationError } from "@/modules/patient-conversations/constants/patient-conversation-error-codes"

interface MenuItem {
  label: string
  icon: Component
  run: () => void
}

const props = defineProps<{
  conversation: PatientConversationDTO
}>()

const visible = ref<boolean>(false)
const mode = ref<"menu" | "reassign">("menu")
const historyVisible = ref<boolean>(false)
const picker = ref<InstanceType<typeof StaffPickerList>>()

const currentStaffId = useCurrentStaffId()
const { data: staffDirectory, isPending: isStaffDirectoryPending } = usePatientConversationStaffDirectory()
const { mutateAsync: changeAssignment, isPending: isChangingAssignment } = useChangePatientConversationAssignment()
const { mutateAsync: changeStatus, isPending: isChangingStatus } = useChangePatientConversationStatus()
const busy = computed<boolean>(() => isChangingAssignment.value || isChangingStatus.value)

// TDP §5.1.6: what the menu offers depends on the status
const items = computed<Array<MenuItem>>(() => {
  const viewHistory: MenuItem = { label: "View history", icon: History, run: openHistory }
  switch (props.conversation.status) {
    case PatientConversationStatusEnum.Open:
      return [
        { label: "Assign", icon: UserRound, run: openReassign },
        { label: "Archive", icon: Archive, run: () => setStatus(PatientConversationStatusEnum.Archived) },
        { label: "Close", icon: CircleCheck, run: confirmClose },
        viewHistory
      ]
    case PatientConversationStatusEnum.Archived:
      return [
        { label: "Unarchive", icon: ArchiveRestore, run: () => setStatus(PatientConversationStatusEnum.Open) },
        { label: "Close", icon: CircleCheck, run: confirmClose },
        viewHistory
      ]
    default:
      return [viewHistory]
  }
})

async function openReassign(): Promise<void> {
  mode.value = "reassign"
  await nextTick()
  picker.value?.focus()
}

function openHistory(): void {
  visible.value = false
  historyVisible.value = true
}

async function onReassign(staffId: string | null): Promise<void> {
  visible.value = false
  try {
    const updated = await changeAssignment({
      patientConversationId: props.conversation.id,
      request: { assignee_id: staffId }
    })
    ElMessage.success(updated.assignee ? `Assigned to ${updated.assignee.full_name}` : "Conversation unassigned")
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}

async function confirmClose(): Promise<void> {
  visible.value = false
  try {
    await ElMessageBox.confirm(
      "Are you sure? The patient will no longer be able to respond.",
      "Close this conversation?",
      {
        confirmButtonText: "Close conversation",
        cancelButtonText: "Cancel",
        type: "warning",
        closeOnClickModal: false
      }
    )
  } catch {
    return
  }
  await setStatus(PatientConversationStatusEnum.Closed)
}

const STATUS_TOASTS: Record<PatientConversationStatusEnum, string> = {
  [PatientConversationStatusEnum.Open]: "Conversation unarchived",
  [PatientConversationStatusEnum.Archived]: "Conversation archived",
  [PatientConversationStatusEnum.Closed]: "Conversation closed"
}

async function setStatus(toStatus: PatientConversationStatusEnum): Promise<void> {
  visible.value = false
  try {
    await changeStatus({ patientConversationId: props.conversation.id, request: { to_status: toStatus } })
    ElMessage.success(STATUS_TOASTS[toStatus])
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.actions-menu
  list-style: none
  margin: 0
  padding: 0

  &__trigger
    display: inline-flex
    align-items: center
    justify-content: center
    width: 28px
    height: 28px
    border: 0
    border-radius: 6px
    background: transparent
    color: $grey
    cursor: pointer

    &:hover
      background-color: $grey-light
      color: $empty-text

  &__item
    display: flex
    align-items: center
    gap: 10px
    width: 100%
    padding: 8px 10px
    border: 0
    border-radius: 6px
    background: transparent
    color: $empty-text
    font-size: 14px
    text-align: left
    cursor: pointer

    &:hover:not(:disabled), &:focus-visible
      background-color: $vitable-green-background

    &:disabled
      color: $grey
      cursor: default

  &__icon
    flex: none
    color: $grey

  &__back
    display: inline-flex
    align-items: center
    gap: 4px
    margin-bottom: 6px
    padding: 2px 4px
    border: 0
    background: transparent
    font-size: 12px
    font-weight: 600
    color: $grey
    cursor: pointer

    &:hover
      color: $empty-text
</style>
